<template>
    <div
        class="redesign-account-menu"
        data-testid="menu-account-container"
    >
        <div class="relative">
            <RedesignBasePopover
                :value="showMenu"
                :disabled="editMode"
                :visible-arrow="false"
                popper-class="account-menu-popover"
                trigger="click"
                width="178"
                @show="showMenu = true"
                @hide="showMenu = false"
            >
                <template #reference>
                    <button
                        :disabled="editMode"
                        :class="{ 'opacity-50': editMode }"
                        data-testid="menu-button-account"
                        class="flex items-center px-3 rounded cursor-pointer focus:ring-1 focus:ring-active-elements border border-ui-lines focus:border-active-elements h-9 outline-0"
                    >
                        <span
                            class="text-color text-start text-main-sm font-semibold truncate"
                            data-testid="menu-account-name"
                            :class="{
                                'w-32': lg,
                                'w-20': !lg
                            }"
                        >
                            {{ selectedAccountData?.dist_name || $t('navbar.default.username') }}
                        </span>
                        <redesign-base-icon
                            name="ChevronDown"
                            class="text-primary transition"
                            :class="{'is-expanded': showMenu}"
                        />
                    </button>
                </template>
                <div
                    class="flex flex-col"
                    data-testid="menu-account-dropdown"
                >
                    <div
                        v-if="allAccounts.length > MIN_ITEMS"
                        class="border-b py-1"
                    >
                        <RedesignBaseInput
                            v-model="searchModel"
                            :placeholder="String($t('general.search'))"
                            data-testid="menu-account-search"
                            prefix-icon="Search"
                            size="small"
                        />
                    </div>
                    <div
                        v-for="(account, index) in allAccountsFiltered"
                        :key="index"
                        :class="{ '!text-primary': selectedAccountID === account.AccountID}"
                        class="text-color hover:text-primary py-2 px-1 cursor-pointer border-b"
                        data-testid="menu-account-item"
                        @click="chooseAccount(account)"
                    >
                        {{ account.dist_name || $t('navbar.default.username') }}
                    </div>
                    <div
                        class="text-color hover:text-primary py-2 px-1 cursor-pointer"
                        @click="logout"
                    >
                        {{ $t('navbar.logout') }}
                    </div>
                </div>
            </RedesignBasePopover>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import { lg } from '@/composables/useBreakpoints'
import {
    clearActiveDashboard,
    resetDashboardCachedDataService,
    selectDashboard
} from '@/composables/globalData/useDashoardsData'
import { startLoading, stopLoading } from '@/composables/useGlobalLoader'
import { setupActiveLayout } from '@/composables/globalData/useLayouts'
import {
    entitiesData,
    selectAccount,
    selectedAccountData,
    selectedAccountID
} from '@/composables/globalData/useEntitiesData'
import { AccountEntity } from '@/types/entities'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import { debouncedWatch } from '@vueuse/core'
import GlobalCachedDataService from '@/core/services/cachedData/GlobalCachedDataService'

const MIN_ITEMS = 9 // + logout

defineProps<{
    editMode?: boolean
}>()
const emit = defineEmits([ 'change:account' ])
/* Data */
const showMenu = ref<boolean>(false)
const searchModel = ref('')
const debouncedSearchModel = ref('')

/* Computed */
const allAccounts = computed(() => {
    return entitiesData.value.Accounts
})
const allAccountsFiltered = computed(() => {
    if (!debouncedSearchModel.value) {
        return allAccounts.value
    }

    return allAccounts.value.filter((account: AccountEntity) => {
        return account.dist_name.toLowerCase().includes(debouncedSearchModel.value.toLowerCase())
    })
})

/* Methods */
async function chooseAccount (account: AccountEntity) {
    try {
        startLoading()
        showMenu.value = false
        if (selectedAccountID.value === account.AccountID) {
            return
        }

        // TODO: all these 3 methods should be in app.vue and injected here
        await selectAccount(account.AccountID)
        await GlobalCachedDataService.loadSelectedAccountEntitiesOnly(selectedAccountID.value)
        await getUserDashboards()
        await setupActiveLayout()
        emit('change:account', account)

    } finally {
        stopLoading()
    }
}

async function getUserDashboards () {
    clearActiveDashboard()
    await resetDashboardCachedDataService()
    await selectDashboard()
}

function logout () {
    localStorage.clear()
    console.log('[LOGOUT!!!!!!!!!!!!!!!!!!]')
    // window.location.href = env.VITE_APP_FALLBACK_URL
}

/* Watcher */
debouncedWatch(
    searchModel,
    (val: string) => {
        debouncedSearchModel.value = val
    },
    { debounce: 300 }
)
</script>

<style lang="scss">
.redesign-account-menu {
    @apply flex relative items-center;

    .is-expanded {
        transform: rotate(-180deg);
    }
}

.redesign-popover.account-menu-popover {
    @apply max-h-72 overflow-y-auto pb-0;
}
</style>
